import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Lock,
  RefreshCw,
  RotateCcw,
} from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { Combobox } from '../../../components/ui/Combobox'
import { Modal } from '../../../components/ui/Modal'
import { Stepper, type Step } from '../../../components/ui/Stepper'
import { cn } from '../../../lib/cn'
import { useAuthStore } from '../../../stores/authStore'
import { counterTone, fieldLimit } from '../../seo-v2/card'
import {
  seoAPI,
  type SeoDesignField,
  type SeoDesignRegenerationApplyResult,
  type SeoDesignRegenerationChannel,
  type SeoDesignRegenerationPreview,
  type SeoDesignRegenerationPreviewRow,
} from '../../seo/api'
import { designsV2API } from '../api'
import {
  buildRegenerateSeoApplyRequest,
  buildRegenerateSeoComment,
  buildRegenerateSeoResultLink,
  editRegenerateSeoRowField,
  groupRegenerateSeoRowsByArticle,
  initRegenerateSeoRows,
  isRegenerateSeoRowDirty,
  isThemeDisplayStopListed,
  normalizeDesignChannelMarketplace,
  resetAllRegenerateSeoRows,
  resetRegenerateSeoRow,
  setRegenerateSeoRowIncluded,
  summarizeRegenerateSeoRows,
  type RegenerateSeoWizardRow,
} from './regenerateSeoWizard.logic'

type WizardStep = 1 | 2 | 3
type LoadState = 'idle' | 'loading' | 'ready' | 'error'

interface RegenerateSeoWizardProps {
  isOpen: boolean
  designCode: number | null
  canRenameTheme: boolean
  onClose: () => void
  onSaved: () => void
}

interface ApprovalSummary {
  approved: number
  skipped: Array<{ article: string; reason: string }>
}

const STEPS: Step[] = [
  { id: 'scope', label: 'Тематика и охват', description: 'Что и где обновить' },
  { id: 'preview', label: 'Предпросмотр', description: 'Проверка и ручная правка' },
  { id: 'confirm', label: 'Утвердить', description: 'Последствия и запись' },
]

const FIELD_LABELS: Record<SeoDesignField, string> = {
  title: 'Наименование',
  description: 'Описание',
}

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error?.message || error?.message || fallback

const channelKey = (channel: SeoDesignRegenerationChannel): string =>
  `${channel.marketplace}:${channel.seller_id}`

function textLimit(row: SeoDesignRegenerationPreviewRow, field: SeoDesignField): number | null {
  return fieldLimit(field, {
    [field]: { policy: { max_length: row.limits[field] } },
  })
}

function Counter({ value, limit }: { value: string; limit: number | null }) {
  const tone = counterTone(value.length, limit)
  return (
    <span
      className={cn(
        'text-2xs tabular-nums',
        tone === 'red' && 'font-semibold text-red-600 dark:text-red-400',
        tone === 'amber' && 'text-amber-700 dark:text-amber-400',
        tone === 'gray' && 'text-gray-400 dark:text-zinc-500',
      )}
    >
      {value.length}{limit == null ? '' : `/${limit}`}
    </span>
  )
}

function RowBadges({
  row,
  selectedFields,
}: {
  row: RegenerateSeoWizardRow
  selectedFields: SeoDesignField[]
}) {
  const warnings = row.preview.issues.filter((issue) => issue.severity === 'warning').length
  const locked = new Set(row.preview.skipped_locked)
  const unchanged = !selectedFields.some(
    (field) => !locked.has(field) && row.fields[field] !== (row.preview.current[field] ?? ''),
  )
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {unchanged && <Badge variant="gray">без изменений</Badge>}
      {row.preview.blocked && <Badge variant="red">блокирующая ошибка</Badge>}
      {warnings > 0 && <Badge variant="yellow">предупреждений: {warnings}</Badge>}
      {row.preview.skipped_locked.length > 0 && (
        <Badge variant="gray" icon={<Lock className="h-3 w-3" />}>
          поле заблокировано
        </Badge>
      )}
      {isRegenerateSeoRowDirty(row) && <Badge variant="indigo">изменено вручную</Badge>}
    </div>
  )
}

export function RegenerateSeoWizard({
  isOpen,
  designCode,
  canRenameTheme,
  onClose,
  onSaved,
}: RegenerateSeoWizardProps) {
  const permissions = useAuthStore((state) => state.user?.permissions || [])
  const canApprove = permissions.includes('Seo:APPROVE')
  const [step, setStep] = useState<WizardStep>(1)
  const [loadState, setLoadState] = useState<LoadState>('idle')
  const [loadError, setLoadError] = useState('')
  const [context, setContext] = useState<SeoDesignRegenerationPreview | null>(null)
  const [themeOptions, setThemeOptions] = useState<string[]>([])
  const [keepTheme, setKeepTheme] = useState(!canRenameTheme)
  const [newTheme, setNewTheme] = useState('')
  const [projectedDesignName, setProjectedDesignName] = useState<string | null>(null)
  const [projectingName, setProjectingName] = useState(false)
  const [selectedChannels, setSelectedChannels] = useState<string[]>([])
  const [selectedFields, setSelectedFields] = useState<SeoDesignField[]>(['title', 'description'])
  const [previewState, setPreviewState] = useState<LoadState>('idle')
  const [previewError, setPreviewError] = useState('')
  const [preview, setPreview] = useState<SeoDesignRegenerationPreview | null>(null)
  const [rows, setRows] = useState<RegenerateSeoWizardRow[]>([])
  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState('')
  const [writtenTheme, setWrittenTheme] = useState<string | null>(null)
  const [result, setResult] = useState<SeoDesignRegenerationApplyResult | null>(null)
  const [approval, setApproval] = useState<ApprovalSummary | null>(null)
  const [approveAfterWrite, setApproveAfterWrite] = useState(false)
  const [refreshingSkipped, setRefreshingSkipped] = useState(false)
  const contextRequestRef = useRef(0)
  const previewRequestRef = useRef(0)
  const code = designCode == null ? '' : String(designCode)

  const notifySaved = useCallback(() => {
    onSaved()
  }, [onSaved])

  const loadContext = useCallback(async () => {
    if (!code) return
    const requestId = ++contextRequestRef.current
    setLoadState('loading')
    setLoadError('')
    try {
      const [sellers, themes] = await Promise.all([
        seoAPI.listSellers(),
        seoAPI.listThemes({ limit: 500 }),
      ])
      const seen = new Set<string>()
      const channels = sellers.flatMap((seller) => {
        const marketplace = normalizeDesignChannelMarketplace(seller.marketplace)
        const sellerId = Number(seller.seller_id)
        if (!marketplace || !Number.isSafeInteger(sellerId) || sellerId <= 0) return []
        const channel = { marketplace, seller_id: sellerId }
        const key = channelKey(channel)
        if (seen.has(key)) return []
        seen.add(key)
        return [channel]
      })
      const data = await seoAPI.previewDesignRegeneration({
        design_code: code,
        channels,
        fields: [],
      })
      if (requestId !== contextRequestRef.current) return
      const options = [...new Set(themes.map((theme) => theme.theme_name?.trim()).filter(Boolean))] as string[]
      setThemeOptions(options)
      setContext(data)
      setNewTheme(data.design.theme)
      setProjectedDesignName(data.design.design_name)
      setSelectedChannels(data.channels.filter((channel) => channel.has_contents).map(channelKey))
      setLoadState('ready')
    } catch (error: any) {
      if (requestId !== contextRequestRef.current) return
      setContext(null)
      setLoadState('error')
      setLoadError(errorMessage(error, 'Не удалось загрузить тематику и каналы макета'))
    }
  }, [code])

  useEffect(() => {
    if (!isOpen || !code) {
      contextRequestRef.current += 1
      previewRequestRef.current += 1
      return
    }
    setStep(1)
    setKeepTheme(!canRenameTheme)
    setSelectedFields(['title', 'description'])
    setPreviewState('idle')
    setPreviewError('')
    setPreview(null)
    setRows([])
    setApplyError('')
    setWrittenTheme(null)
    setResult(null)
    setApproval(null)
    setApproveAfterWrite(false)
    void loadContext()
    return () => {
      contextRequestRef.current += 1
      previewRequestRef.current += 1
    }
  }, [isOpen, code, canRenameTheme, loadContext])

  const availableChannels = context?.channels || []
  const chosenChannels = useMemo(
    () => availableChannels
      .filter((channel) => selectedChannels.includes(channelKey(channel)))
      .map(({ marketplace, seller_id }) => ({ marketplace, seller_id })),
    [availableChannels, selectedChannels],
  )
  const themeForPreview = !keepTheme && canRenameTheme ? newTheme.trim() : undefined
  const themeChanged = Boolean(
    themeForPreview
    && themeForPreview !== String(context?.design.theme || '').trim(),
  )

  useEffect(() => {
    if (!isOpen || loadState !== 'ready' || !context || keepTheme || !canRenameTheme) {
      setProjectedDesignName(context?.design.design_name ?? null)
      setProjectingName(false)
      return
    }
    const theme = newTheme.trim()
    if (!theme) {
      setProjectingName(false)
      setProjectedDesignName(context.design.design_name)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      setProjectingName(true)
      try {
        const data = await seoAPI.previewDesignRegeneration({
          design_code: code,
          theme,
          channels: availableChannels.map(({ marketplace, seller_id }) => ({ marketplace, seller_id })),
          fields: [],
        })
        if (!cancelled) setProjectedDesignName(data.design.next_design_name)
      } catch {
        if (!cancelled) setProjectedDesignName(context.design.design_name)
      } finally {
        if (!cancelled) setProjectingName(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [
    isOpen,
    loadState,
    context,
    keepTheme,
    canRenameTheme,
    newTheme,
    code,
    availableChannels,
  ])

  const requestPreview = useCallback(async () => {
    if (!code || !chosenChannels.length || !selectedFields.length) return
    const requestId = ++previewRequestRef.current
    setPreviewState('loading')
    setPreviewError('')
    try {
      const data = await seoAPI.previewDesignRegeneration({
        design_code: code,
        ...(themeForPreview ? { theme: themeForPreview } : {}),
        channels: chosenChannels,
        fields: selectedFields,
      })
      if (requestId !== previewRequestRef.current) return
      setPreview(data)
      setRows(initRegenerateSeoRows(data.rows))
      setProjectedDesignName(data.design.next_design_name)
      setPreviewState('ready')
      setStep(2)
    } catch (error: any) {
      if (requestId !== previewRequestRef.current) return
      setPreviewState('error')
      setPreviewError(errorMessage(error, 'Не удалось построить предпросмотр SEO'))
    }
  }, [code, chosenChannels, selectedFields, themeForPreview])

  const summary = useMemo(
    () => summarizeRegenerateSeoRows(rows, selectedFields),
    [rows, selectedFields],
  )
  const groups = useMemo(() => groupRegenerateSeoRowsByArticle(rows), [rows])
  const applyRequest = useMemo(() => buildRegenerateSeoApplyRequest({
    designCode: code,
    rows,
    fields: selectedFields,
    comment: buildRegenerateSeoComment(context?.design.theme, themeForPreview),
  }), [code, rows, selectedFields, context?.design.theme, themeForPreview])

  const approveWrittenRows = async (written: SeoDesignRegenerationApplyResult['written_rows']) => {
    const batches = new Map<string, SeoDesignRegenerationApplyResult['written_rows']>()
    for (const row of written) {
      const key = `${row.marketplace}:${row.seller_id}`
      batches.set(key, [...(batches.get(key) || []), row])
    }
    let approved = 0
    const skipped: ApprovalSummary['skipped'] = []
    for (const batch of batches.values()) {
      const first = batch[0]
      try {
        const response = await seoAPI.approveDrafts({
          marketplace: first.marketplace,
          seller_id: first.seller_id,
          articles: batch.map((row) => row.article),
        })
        approved += response.approved
        skipped.push(...response.skipped)
      } catch (error: any) {
        const reason = errorMessage(error, 'Не удалось согласовать записанные тексты')
        skipped.push(...batch.map((row) => ({ article: row.article, reason })))
      }
    }
    return { approved, skipped }
  }

  const apply = async () => {
    if (!code || applyRequest.rows.length === 0) return
    setApplying(true)
    setApplyError('')
    let wroteThemeNow = false
    try {
      if (themeChanged && writtenTheme !== themeForPreview) {
        await designsV2API.updateDesign(code, { theme: themeForPreview })
        wroteThemeNow = true
        setWrittenTheme(themeForPreview || null)
        setContext((current) => current ? {
          ...current,
          design: {
            ...current.design,
            theme: themeForPreview || current.design.theme,
            theme_source: 'base_designs.theme',
            design_name: projectedDesignName || current.design.design_name,
            next_design_name: projectedDesignName || current.design.next_design_name,
          },
        } : current)
      }
      const response = await seoAPI.applyDesignRegeneration(applyRequest)
      setResult(response)
      if (approveAfterWrite && canApprove && response.written_rows.length > 0) {
        setApproval(await approveWrittenRows(response.written_rows))
      }
      notifySaved()
    } catch (error: any) {
      if (wroteThemeNow || writtenTheme === themeForPreview) {
        notifySaved()
        setApplyError(
          `Тематика сохранена, но SEO-тексты не записаны. ${errorMessage(error, 'Повторите запись SEO')}`,
        )
      } else {
        setApplyError(errorMessage(error, 'Не удалось записать изменения'))
      }
    } finally {
      setApplying(false)
    }
  }

  const retrySkipped = async () => {
    if (!result?.skipped.length) return
    const requestId = ++previewRequestRef.current
    setRefreshingSkipped(true)
    setApplyError('')
    try {
      const skippedIds = new Set(result.skipped.map((item) => item.content_id))
      const edited = new Map(rows.map((row) => [row.preview.content_id, row]))
      const data = await seoAPI.previewDesignRegeneration({
        design_code: code,
        ...(themeForPreview ? { theme: themeForPreview } : {}),
        channels: chosenChannels,
        fields: selectedFields,
      })
      if (requestId !== previewRequestRef.current) return
      const refreshed = initRegenerateSeoRows(data.rows).map((row) => {
        const previous = edited.get(row.preview.content_id)
        return {
          ...row,
          included: skippedIds.has(row.preview.content_id),
          fields: previous && skippedIds.has(row.preview.content_id) ? previous.fields : row.fields,
        }
      })
      setPreview(data)
      setRows(refreshed)
      setResult(null)
      setApproval(null)
      setStep(2)
    } catch (error: any) {
      if (requestId !== previewRequestRef.current) return
      setApplyError(errorMessage(error, 'Не удалось обновить предпросмотр для повторной записи'))
    } finally {
      if (requestId === previewRequestRef.current) setRefreshingSkipped(false)
    }
  }

  const resultLink = useMemo(() => {
    const target = result?.written_rows.find((row) => row.marketplace !== 'site')
    return buildRegenerateSeoResultLink(target)
  }, [result])

  const footer = result ? (
    <div className="flex w-full flex-wrap items-center gap-2">
      {result.skipped.length > 0 && (
        <Button
          variant="secondary"
          loading={refreshingSkipped}
          onClick={() => void retrySkipped()}
        >
          Повторить запись
        </Button>
      )}
      <Button variant="primary" className="sm:ml-auto" onClick={onClose}>
        Закрыть
      </Button>
    </div>
  ) : (
    <div className="flex w-full flex-wrap items-center gap-2">
      <Button variant="secondary" onClick={onClose} disabled={applying}>
        Отмена
      </Button>
      <Button
        variant="secondary"
        disabled={step === 1 || applying || previewState === 'loading'}
        onClick={() => {
          setApplyError('')
          setStep((current) => (current > 1 ? ((current - 1) as WizardStep) : current))
        }}
      >
        Назад
      </Button>
      {step === 1 && (
        <Button
          variant="primary"
          className="sm:ml-auto"
          loading={previewState === 'loading'}
          disabled={
            loadState !== 'ready'
            || chosenChannels.length === 0
            || selectedFields.length === 0
            || (!keepTheme && canRenameTheme && !newTheme.trim())
          }
          onClick={() => void requestPreview()}
        >
          Предпросмотр
        </Button>
      )}
      {step === 2 && (
        <Button
          variant="primary"
          className="sm:ml-auto"
          disabled={applyRequest.rows.length === 0}
          onClick={() => setStep(3)}
        >
          Далее
        </Button>
      )}
      {step === 3 && (
        <Button
          variant="primary"
          className="sm:ml-auto"
          loading={applying}
          disabled={applyRequest.rows.length === 0}
          onClick={() => void apply()}
        >
          Утвердить и записать
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={applying ? () => undefined : onClose}
      title="Перегенерировать SEO"
      subtitle={code ? `Макет #${code}` : undefined}
      size="full"
      layer="nested"
      footer={footer}
      closeOnOverlayClick={false}
      closeOnEsc={!applying}
      showCloseButton={!applying}
    >
      <div className="flex gap-6">
        {!result && (
          <div className="hidden w-48 shrink-0 pt-1 md:block">
            <Stepper
              steps={STEPS}
              currentIndex={step - 1}
              onStepClick={(index) => {
                const target = (index + 1) as WizardStep
                if (target < step && !applying) setStep(target)
              }}
            />
          </div>
        )}

        <div className="min-w-0 flex-1">
          {!result && (
            <div className="mb-4 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 dark:bg-zinc-800 dark:text-zinc-200 md:hidden">
              Шаг {step} из 3 · {STEPS[step - 1].label}
            </div>
          )}

          {loadState === 'loading' && (
            <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Загружаем тематику и каналы макета…
            </p>
          )}
          {loadState === 'error' && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              <p>{loadError}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => void loadContext()}>
                <RefreshCw className="h-4 w-4" /> Повторить
              </Button>
            </div>
          )}

          {step === 1 && loadState === 'ready' && context && !result && (
            <div className="space-y-5">
              <section className="rounded-xl border border-gray-200 p-4 dark:border-zinc-800">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Сейчас</h4>
                <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-[10rem_1fr]">
                  <dt className="text-gray-500 dark:text-zinc-400">Тематика</dt>
                  <dd>
                    <span className="font-medium">{context.design.theme || '—'}</span>
                    <code className="ml-2 text-2xs text-gray-400 dark:text-zinc-500">
                      {context.design.theme_source}
                    </code>
                    {context.design.theme_source === 'base_designs.design_name' && (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                        Тематика не заполнена, для SEO используется наименование макета.
                      </p>
                    )}
                  </dd>
                  <dt className="text-gray-500 dark:text-zinc-400">Тематика №</dt>
                  <dd>
                    {context.design.theme_number ?? '—'}
                    {context.design.siblings > 0 && (
                      <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                        ещё {context.design.siblings} макетов — они не изменятся
                      </span>
                    )}
                    <a className="ml-2 text-xs text-indigo-600 hover:underline dark:text-indigo-400" href="/themes-board">
                      Доска тематик
                    </a>
                  </dd>
                  <dt className="text-gray-500 dark:text-zinc-400">Наименование</dt>
                  <dd>{context.design.design_name || '—'}</dd>
                </dl>
              </section>

              <section className="rounded-xl border border-gray-200 p-4 dark:border-zinc-800">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Станет</h4>
                <label className="mt-3 flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={keepTheme}
                    onChange={(event) => setKeepTheme(event.target.checked)}
                    disabled={!canRenameTheme || previewState === 'loading'}
                    className="mt-0.5 h-4 w-4 accent-indigo-600"
                  />
                  <span>
                    <span className="font-medium">Оставить тематику как есть</span>
                    <span className="block text-xs text-gray-500 dark:text-zinc-400">
                      Перегенерировать тексты без переименования макета.
                    </span>
                  </span>
                </label>
                <div className="mt-3 max-w-xl">
                  <label htmlFor="regenerate-seo-theme" className="mb-1 block text-xs font-medium text-gray-600 dark:text-zinc-300">
                    Новая тематика
                  </label>
                  <Combobox
                    id="regenerate-seo-theme"
                    value={newTheme}
                    onChange={setNewTheme}
                    options={themeOptions}
                    allowCustom
                    disabled={keepTheme || !canRenameTheme || previewState === 'loading'}
                    panelClassName="z-[100]"
                    placeholder="Например, Бен Тен"
                  />
                  {!canRenameTheme && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                      Для смены тематики требуется право Product:UPDATE. Перегенерация без переименования доступна.
                    </p>
                  )}
                  {isThemeDisplayStopListed(themeForPreview) && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Для этого значения канонический тематический запрос строиться не будет.
                    </p>
                  )}
                </div>
                <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-zinc-800/70">
                  <span className="text-gray-500 dark:text-zinc-400">Наименование макета: </span>
                  <span className="font-medium">{projectedDesignName || context.design.design_name || '—'}</span>
                  {projectingName && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-gray-400" />}
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 p-4 dark:border-zinc-800">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Охват</h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  По умолчанию отмечены каналы, где у макета уже есть SEO-представления.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {availableChannels.map((channel) => {
                    const key = channelKey(channel)
                    const checked = selectedChannels.includes(key)
                    return (
                      <label key={key} className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={previewState === 'loading'}
                          onChange={(event) => setSelectedChannels((current) => event.target.checked
                            ? [...current, key]
                            : current.filter((item) => item !== key))}
                          className="h-4 w-4 accent-indigo-600"
                        />
                        <span className="min-w-0 flex-1 truncate">{channel.seller_name}</span>
                        <span className="text-2xs uppercase text-gray-400">{channel.marketplace}</span>
                        {!channel.has_contents && <Badge variant="gray">новый</Badge>}
                      </label>
                    )
                  })}
                </div>
                {availableChannels.length === 0 && (
                  <p className="mt-3 text-sm text-red-600 dark:text-red-400">Нет доступных активных каналов.</p>
                )}
                <div className="mt-4 flex flex-wrap gap-4">
                  {(['title', 'description'] as SeoDesignField[]).map((field) => (
                    <label key={field} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field)}
                        disabled={previewState === 'loading'}
                        onChange={(event) => setSelectedFields((current) => event.target.checked
                          ? [...current, field]
                          : current.filter((item) => item !== field))}
                        className="h-4 w-4 accent-indigo-600"
                      />
                      {FIELD_LABELS[field]}
                    </label>
                  ))}
                </div>
                <p className="mt-4 text-sm font-medium text-gray-700 dark:text-zinc-300">
                  {context.products} товаров × {chosenChannels.length} каналов = {context.products * chosenChannels.length} карточек
                </p>
              </section>

              {previewState === 'error' && (
                <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  {previewError}
                </p>
              )}
            </div>
          )}

          {step === 2 && previewState === 'ready' && preview && !result && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 p-3 dark:border-zinc-800">
                <Badge variant="green">изменится: {summary.changed}</Badge>
                <Badge variant="gray">без изменений: {summary.unchanged}</Badge>
                <Badge variant="red">с блокирующими: {summary.blocked}</Badge>
                <span className="text-xs text-gray-500 dark:text-zinc-400">выбрано строк: {summary.total}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  className="sm:ml-auto"
                  onClick={() => setRows((current) => resetAllRegenerateSeoRows(current))}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Вернуть всё сгенерированное
                </Button>
              </div>

              {preview.missing_contents > 0 && (
                <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
                  Заведено новых SEO-представлений: {preview.missing_contents}. Они создаются
                  предпросмотром и остаются пустыми, пока текст не записан.
                </p>
              )}

              <div className="space-y-5">
                {groups.map((group) => (
                  <section key={group.article} className="overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-800">
                    <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-zinc-800 dark:bg-zinc-800/60">
                      <p className="font-mono text-sm font-semibold">{group.article}</p>
                      {group.productTitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">{group.productTitle}</p>}
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-zinc-800">
                      {group.rows.map((row) => {
                        const titleLocked = row.preview.skipped_locked.includes('title')
                        const descriptionLocked = row.preview.skipped_locked.includes('description')
                        const titleLimit = textLimit(row.preview, 'title')
                        const descriptionLimit = textLimit(row.preview, 'description')
                        return (
                          <div key={row.preview.content_id} className={cn('p-4', !row.included && 'opacity-60')}>
                            <div className="flex flex-wrap items-start gap-3">
                              <label className="flex min-w-36 items-center gap-2 text-sm font-medium">
                                <input
                                  type="checkbox"
                                  checked={row.included}
                                  onChange={(event) => setRows((current) => setRegenerateSeoRowIncluded(
                                    current,
                                    row.preview.content_id,
                                    event.target.checked,
                                  ))}
                                  className="h-4 w-4 accent-indigo-600"
                                />
                                {row.preview.seller_name}
                              </label>
                              <RowBadges row={row} selectedFields={selectedFields} />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="sm:ml-auto"
                                disabled={!isRegenerateSeoRowDirty(row)}
                                onClick={() => setRows((current) => resetRegenerateSeoRow(current, row.preview.content_id))}
                              >
                                <RotateCcw className="h-3.5 w-3.5" /> Вернуть сгенерированное
                              </Button>
                            </div>

                            {selectedFields.includes('title') && (
                              <div className="mt-3 grid gap-2 lg:grid-cols-2">
                                <div>
                                  <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-gray-400">Было</p>
                                  <div className="min-h-20 rounded-md bg-gray-50 p-2 text-sm text-gray-600 dark:bg-zinc-800/60 dark:text-zinc-300">
                                    {row.preview.current.title || '—'}
                                  </div>
                                </div>
                                <div>
                                  <div className="mb-1 flex items-center justify-between">
                                    <p className="text-2xs font-semibold uppercase tracking-wide text-gray-400">Станет</p>
                                    <Counter value={row.fields.title} limit={titleLimit} />
                                  </div>
                                  <textarea
                                    aria-label={`Наименование ${row.preview.article} ${row.preview.seller_name}`}
                                    rows={3}
                                    value={row.fields.title}
                                    disabled={!row.included || titleLocked}
                                    onChange={(event) => setRows((current) => editRegenerateSeoRowField(
                                      current,
                                      row.preview.content_id,
                                      'title',
                                      event.target.value,
                                    ))}
                                    className="w-full resize-y rounded-md border border-gray-300 bg-white p-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 dark:border-zinc-700 dark:bg-zinc-900 dark:disabled:bg-zinc-800"
                                  />
                                </div>
                              </div>
                            )}

                            {selectedFields.includes('description') && (
                              <details className="group mt-3 rounded-lg border border-gray-200 dark:border-zinc-800">
                                <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs font-medium text-gray-600 dark:text-zinc-300">
                                  <ChevronDown className="h-3.5 w-3.5 group-open:hidden" />
                                  <ChevronUp className="hidden h-3.5 w-3.5 group-open:block" />
                                  Описание: было / станет
                                </summary>
                                <div className="grid gap-3 border-t border-gray-200 p-3 dark:border-zinc-800 lg:grid-cols-2">
                                  <div className="whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-sm text-gray-600 dark:bg-zinc-800/60 dark:text-zinc-300">
                                    {row.preview.current.description || '—'}
                                  </div>
                                  <div>
                                    <div className="mb-1 flex justify-end">
                                      <Counter value={row.fields.description} limit={descriptionLimit} />
                                    </div>
                                    <textarea
                                      aria-label={`Описание ${row.preview.article} ${row.preview.seller_name}`}
                                      rows={8}
                                      value={row.fields.description}
                                      disabled={!row.included || descriptionLocked}
                                      onChange={(event) => setRows((current) => editRegenerateSeoRowField(
                                        current,
                                        row.preview.content_id,
                                        'description',
                                        event.target.value,
                                      ))}
                                      className="w-full resize-y rounded-md border border-gray-300 bg-white p-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 dark:border-zinc-700 dark:bg-zinc-900 dark:disabled:bg-zinc-800"
                                    />
                                  </div>
                                </div>
                              </details>
                            )}

                            {row.preview.issues.length > 0 && (
                              <ul className="mt-3 space-y-1 text-xs text-gray-600 dark:text-zinc-300">
                                {row.preview.issues.map((issue, index) => (
                                  <li key={`${issue.code}:${index}`} className="flex gap-2">
                                    <span className={issue.severity === 'critical' ? 'text-red-600' : issue.severity === 'warning' ? 'text-amber-600' : 'text-gray-400'}>
                                      {issue.severity === 'critical' ? 'Ошибка' : issue.severity === 'warning' ? 'Предупреждение' : 'Подсказка'}:
                                    </span>
                                    <span>{issue.message}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          )}

          {step === 3 && previewState === 'ready' && context && !result && (
            <div className="space-y-4">
              <div className="rounded-xl border border-gray-200 p-4 dark:border-zinc-800">
                <h4 className="text-sm font-semibold">После подтверждения</h4>
                <ul className="mt-3 space-y-2 text-sm text-gray-700 dark:text-zinc-300">
                  <li>
                    Тематика макета: «{context.design.theme}» → «{themeChanged ? themeForPreview : context.design.theme}».
                  </li>
                  <li>Наименование макета пересоберётся: «{projectedDesignName || context.design.design_name || '—'}».</li>
                  <li>Будет записано {applyRequest.rows.length} черновиков в {chosenChannels.length} каналах.</li>
                  <li>Ранее согласованные карточки вернутся в состояние «черновик».</li>
                  <li className="font-medium">На площадки ничего не отправляется.</li>
                </ul>
              </div>

              <label className="flex items-start gap-2 rounded-xl border border-gray-200 p-4 text-sm dark:border-zinc-800">
                <input
                  type="checkbox"
                  checked={approveAfterWrite}
                  onChange={(event) => setApproveAfterWrite(event.target.checked)}
                  disabled={!canApprove}
                  className="mt-0.5 h-4 w-4 accent-indigo-600"
                />
                <span>
                  <span className="font-medium">Согласовать тексты после записи</span>
                  <span className="block text-xs text-gray-500 dark:text-zinc-400">
                    По умолчанию выключено. Согласование выполняется отдельно и требует Seo:APPROVE.
                  </span>
                  {!canApprove && (
                    <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
                      У вас нет права на согласование; черновики всё равно можно записать.
                    </span>
                  )}
                </span>
              </label>

              {applyError && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  <p>{applyError}</p>
                  {writtenTheme === themeForPreview && (
                    <Button variant="secondary" size="sm" className="mt-3" onClick={() => void apply()} loading={applying}>
                      Повторить запись SEO
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="space-y-5">
              <div className={cn(
                'flex items-start gap-3 rounded-xl border p-4',
                result.written > 0
                  ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/30 dark:text-green-300'
                  : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300',
              )}>
                {result.written > 0
                  ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />}
                <div>
                  <h4 className="font-semibold">
                    {result.written > 0 ? 'SEO-черновики записаны' : 'SEO-черновики не записаны'}
                  </h4>
                  <p className="mt-1 text-sm">Записано: {result.written}. Пропущено: {result.skipped.length}.</p>
                  {approval && <p className="mt-1 text-sm">Согласовано: {approval.approved}. Не согласовано: {approval.skipped.length}.</p>}
                </div>
              </div>

              {result.skipped.length > 0 && (
                <section className="rounded-xl border border-amber-300 p-4 dark:border-amber-900">
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Пропущенные строки</h4>
                  <ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-zinc-300">
                    {result.skipped.map((item) => (
                      <li key={item.content_id}>#{item.content_id}: {item.reason}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
                    Перед повтором предпросмотр обновится, чтобы получить актуальные версии черновиков.
                  </p>
                </section>
              )}

              {approval?.skipped.length ? (
                <section className="rounded-xl border border-amber-300 p-4 text-sm dark:border-amber-900">
                  <h4 className="font-semibold text-amber-800 dark:text-amber-300">Не удалось согласовать</h4>
                  <ul className="mt-2 space-y-1">
                    {approval.skipped.map((item, index) => <li key={`${item.article}:${index}`}>{item.article}: {item.reason}</li>)}
                  </ul>
                </section>
              ) : null}

              {resultLink && (
                <a className="inline-flex text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400" href={resultLink}>
                  Открыть в SEO-модуле
                </a>
              )}

              {applyError && <p className="text-sm text-red-600 dark:text-red-400">{applyError}</p>}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default RegenerateSeoWizard
